'use client'

import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { useMutation } from '@apollo/client'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { UpdateIcon } from '@radix-ui/react-icons'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { gql } from '@apollo/client'
import {
  loadSofaElement,
  type ModuleExports,
} from '@/configurator/DynamicLoadSofaShape'
import {
  applyChangeableOverrides,
  getDefaultDimensionOverrides,
  getShapeChangeableProperties,
  type ChangeableProperties,
} from '@/configurator/utils/shapeConfigUtils'
import { SofaFormTypeGridSelect } from '@/components/shared/inputComponents/SofaFormTypeGridSelect'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import SofaFormEditForm from './SofaFormEditForm'
import SofaDimensionsBlueprintField from './SofaDimensionsBlueprintField'
import {
  dimensionFieldsConfig,
  DimensionFieldItem,
  isOnBlueprintKey,
  ExtensionType,
  BackrestType,
} from './dimensionFields'

const CREATE_SOFA_SHAPE = gql`
  mutation CreateNewSofaShape($advancedProductId: Float!, $sofa_shape: SofaShapeCreateInput!) {
    createNewSofaShape(advancedProductId: $advancedProductId, sofa_shape: $sofa_shape) {
      id
    }
  }
`

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.string().min(1, 'Type is required'),
  code: z.string().optional().nullable(),
  left_and_right: z.boolean(),
  enabled: z.boolean().default(true),
  dimensions: z
    .object({
      height: z.number().min(0).max(1000).nullable(),
      width: z.number().min(0).max(1000).nullable(),
      length: z.number().min(0).max(1000).nullable(),
      seat_height: z.number().min(0).max(1000).nullable(),
      seat_depth: z.number().min(0).max(1000).nullable(),
      seat_width: z.number().min(0).max(1000).nullable(),
      armrest_width: z.number().min(0).max(1000).nullable(),
      backrest_width: z.number().min(0).max(1000).nullable(),
      corner_radius: z.number().min(0).max(1000).nullable(),
      number_of_big_pillows: z.number().min(0).max(100).nullable(),
      number_of_small_pillows: z.number().min(0).max(100).nullable(),
      spread_of_small_pillows: z.number().min(0).max(1000).nullable(),
      spread_of_big_pillows: z.number().min(0).max(1000).nullable(),
      size_of_big_pillow: z.number().min(0).max(1000).nullable(),
      size_of_pillow: z.number().min(0).max(1000).nullable(),
      angle: z.number().min(0).max(360).nullable(),
      extendable_part_length: z.number().min(0).max(1000).nullable(),
      mattress_width: z.number().min(0).max(1000).nullable(),
      mattress_length: z.number().min(0).max(1000).nullable(),
      corner_part_length: z.number().min(0).max(1000).nullable(),
      seat_sections: z.number().min(0).max(100).nullable(),
      backrest_sections: z.number().min(0).max(100).nullable(),
      extension_type: z.nativeEnum(ExtensionType),
      backrest_type: z.nativeEnum(BackrestType),
      covered_side: z.boolean().nullable(),
    })
    .default({
      height: null,
      width: null,
      length: null,
      seat_height: null,
      seat_depth: null,
      seat_width: null,
      armrest_width: null,
      backrest_width: null,
      corner_radius: null,
      number_of_big_pillows: null,
      number_of_small_pillows: null,
      spread_of_small_pillows: null,
      spread_of_big_pillows: null,
      size_of_big_pillow: null,
      size_of_pillow: null,
      angle: null,
      extendable_part_length: null,
      mattress_width: null,
      mattress_length: null,
      corner_part_length: null,
      seat_sections: null,
      backrest_sections: null,
      extension_type: ExtensionType.STANDART,
      backrest_type: BackrestType.STANDART,
      covered_side: null,
    }),
})

interface SofaFormCreateFormProps {
  data: {
    type: string
    advancedProductId: number
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function SofaFormCreateForm({
  data: { type, advancedProductId },
  onDirtyChange,
}: SofaFormCreateFormProps) {
  const [changeableProperties, setChangeableProperties] =
    useState<ChangeableProperties>({
      width: true,
      height: true,
      length: true,
      seat_height: true,
      seat_depth: true,
      seat_width: true,
      corner_radius: true,
      armrest_width: true,
      backrest_width: true,
      number_of_big_pillows: true,
      number_of_small_pillows: true,
      spread_of_small_pillows: true,
      spread_of_big_pillows: true,
      size_of_big_pillow: true,
      size_of_pillow: true,
      extension_type: true,
      backrest_type: true,
      angle: true,
      covered_side: true,
    })
  // Type the current changeableProperties belong to (gates blueprint anchors).
  const [propertiesType, setPropertiesType] = useState<string | null>(null)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      type: type,
      code: '',
      left_and_right: false,
      enabled: true,
      dimensions: {
        height: null,
        width: null,
        length: null,
        seat_height: null,
        seat_depth: null,
        seat_width: null,
        armrest_width: null,
        backrest_width: null,
        corner_radius: null,
        number_of_big_pillows: null,
        number_of_small_pillows: null,
        spread_of_small_pillows: null,
        spread_of_big_pillows: null,
        size_of_big_pillow: null,
        size_of_pillow: null,
        angle: null,
        extendable_part_length: null,
        mattress_width: null,
        mattress_length: null,
        corner_part_length: null,
        seat_sections: null,
        backrest_sections: null,
        extension_type: ExtensionType.STANDART,
        backrest_type: BackrestType.STANDART,
        covered_side: null,
      },
    },
  })

  const openSofaFormEdit = useOpenDrawer(SofaFormEditForm)

  const [createSofaShape, { loading: isSaving }] = useMutation(
    CREATE_SOFA_SHAPE,
    {
      onError: () => {
        toast.error('Failed to create sofa module')
      },
      refetchQueries: ['GetSofaForms', 'GET_PRODUCT_DETAILS'],
    }
  )

  const watchedType = form.watch('type')

  // Load default settings and changeable properties from the shape module
  useEffect(() => {
    const currentType = watchedType
    if (!currentType) return

    const loadShapeDefaults = async () => {
      try {
        const moduleExports = await loadSofaElement(currentType)
        if (form.getValues('type') !== currentType) return
        if (moduleExports?.getDefaultSettings) {
          const settings = moduleExports.getDefaultSettings()
          const defaults = settings.dimensions
            ? ({
                ...settings.dimensions,
                ...getDefaultDimensionOverrides(currentType),
              } as typeof settings.dimensions & {
                armrest_width?: number | null
                backrest_width?: number | null
              })
            : undefined

          // Pre-fill dimensions from defaults so the on-blueprint inputs show
          // the same values the drawing uses.
          if (defaults) {
            const currentDimensions = form.getValues('dimensions')
            form.reset({
              ...form.getValues(),
              dimensions: {
                ...currentDimensions,
                width: defaults.width ?? currentDimensions.width,
                length: defaults.length ?? currentDimensions.length,
                corner_radius:
                  defaults.corner_radius ?? currentDimensions.corner_radius,
                armrest_width:
                  defaults.armrest_width ?? currentDimensions.armrest_width,
                backrest_width:
                  defaults.backrest_width ?? currentDimensions.backrest_width,
              },
            })
          }

          if (settings.changeableProperties) {
            setChangeableProperties(
              applyChangeableOverrides(
                currentType,
                settings.changeableProperties
              )
            )
          } else {
            setChangeableProperties(
              await getShapeChangeableProperties(currentType)
            )
          }
        } else {
          // Fallback: load changeable properties separately
          const properties = await getShapeChangeableProperties(currentType)
          setChangeableProperties(properties)
        }
        setPropertiesType(currentType)
      } catch (error) {
        console.error('Error loading shape defaults:', error)
      }
    }

    loadShapeDefaults()
  }, [watchedType])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  async function onSubmit(values: z.infer<typeof formSchema>) {
    let result
    try {
      result = await createSofaShape({
        variables: {
          advancedProductId,
          sofa_shape: {
            name: values.name,
            type: values.type,
            code: values.code || null,
            left_and_right: values.left_and_right,
            enabled: values.enabled,
            duplicated: false,
            unique_marker: crypto.randomUUID(),
            dimensions: values.dimensions,
          },
        },
      })
    } catch {
      // onError already surfaced the failure toast.
      return
    }

    const newId = result.data?.createNewSofaShape?.id
    if (!newId) return

    toast.success('Sofa module created successfully')
    onDirtyChange?.(false)
    openSofaFormEdit({
      id: Number(newId),
      name: values.name,
      type: values.type,
      code: values.code ?? '',
      left_and_right: values.left_and_right,
      enabled: values.enabled,
      dimensions: values.dimensions,
    })
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <SheetTitle>Create New Module</SheetTitle>
        <SheetDescription>Type: {watchedType}</SheetDescription>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Type"
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <SofaFormTypeGridSelect field={field} />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="2D Blueprint"
                control={form.control}
                name="dimensions"
                render={() => (
                  <FormItem className="col-span-3">
                    <SofaDimensionsBlueprintField
                      id={0}
                      changeableProperties={changeableProperties}
                      propertiesType={propertiesType}
                    />
                  </FormItem>
                )}
              />

              <Separator />
              <div className="grid grid-cols-2 gap-4">
                {dimensionFieldsConfig.map(({ key, label, measurement }) => {
                  if (!changeableProperties[key]) return null
                  if (isOnBlueprintKey(key, watchedType)) return null
                  return (
                    <InputFieldContainer
                      key={key}
                      label={label}
                      variant="two-column"
                      control={form.control}
                      name={`dimensions.${key}` as any}
                      render={({ field }) => (
                        <DimensionFieldItem
                          fieldKey={key}
                          measurement={measurement}
                          field={field}
                        />
                      )}
                    />
                  )
                })}
              </div>
              <Separator />
              <InputFieldContainer
                label="Name"
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter module name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter module code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Left and Right"
                control={form.control}
                name="left_and_right"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 py-1.5">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Enabled"
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 py-1.5">
                    <FormControl>
                      <Switch
                        variant="green"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="submit" disabled={isSaving}>
                  {isSaving && (
                    <UpdateIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isSaving ? 'Creating...' : 'Create Module'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
